import React, { useMemo } from 'react';
import QRCode from 'qrcode.react';
import { decodeHtml } from '../../utils/formatters';

const StickerPreview = React.memo(({
  layout = 'classic',
  data = {},
  showQR = true,
  qrPosition = 'top', // 'top', 'bottom', 'left', 'right'
  className = '',
  size = 'normal', // 'small', 'normal', 'large'
  customWidth = null, // Custom width in pixels
  customHeight = null, // Custom height in pixels
  colorMode = 'normal', // 'normal' | 'dark'
  nameFontSize = null, // Custom name font size in px
  positionFontSize = null, // Custom position font size in px
  orgFontSize = null, // Custom organization font size in px
  lineHeight = 1.2, // Line height multiplier
  logoUrl = null, // Logo URL
  logoSize = 40, // Logo size in px
  showLogo = false, // Show logo
  autoScaleFont = true, // Auto-scale font for long text
  nameFontWeight = 'bold', // Font weight for name: 'normal', 'medium', 'semibold', 'bold', 'extrabold'
  positionFontWeight = 'semibold', // Font weight for position
  orgFontWeight = 'semibold', // Font weight for organization
  showPosition = true, // Show position field
  showOrganization = true // Show organization field
}: any) => {
  // Helper function to convert font weight name to CSS value
  const getFontWeightValue = (weight) => {
    const weights = {
      'normal': 400,
      'medium': 500,
      'semibold': 600,
      'bold': 700,
      'extrabold': 800
    };
    return weights[weight] || 700; // Default to bold if not found
  };
  // Default data if not provided
  const stickerData = {
    name: data.name || 'SAMPLE NAME',
    position: data.position || data.title || 'Position Title',
    organization: data.organization || data.company || 'Organization Name',
    attendeeId: data.attendeeId || '12345',
    ...data
  };

  // Size configurations based on real-world sticker dimensions (96 DPI)
  // These are actual printing sizes that will be used for physical stickers
  const sizeConfig = {
    tiny: {
      // 1" x 1" square sticker (96px x 96px at 96 DPI)
      container: '',
      width: 96,
      height: 96,
      qrSize: 36,
      nameSize: 'text-xs',
      positionSize: 'text-xs',
      orgSize: 'text-xs',
      padding: 'p-2',
      spacing: 'gap-2'
    },
    small: {
      // 1.5" x 1" rectangular sticker (144px x 96px at 96 DPI)
      container: '',
      width: 144,
      height: 96,
      qrSize: 42,
      nameSize: 'text-xs',
      positionSize: 'text-xs',
      orgSize: 'text-xs',
      padding: 'p-2',
      spacing: 'gap-2'
    },
    '40x20mm': {
      // 40mm × 20mm sticker (151px × 76px at 96 DPI)
      container: '',
      width: 151,
      height: 76,
      qrSize: 44,
      nameSize: 'text-xs',
      positionSize: 'text-xs',
      orgSize: 'text-xs',
      padding: 'p-2',
      spacing: 'gap-2'
    },
    '40x30mm': {
      // 40mm × 30mm sticker (151px × 113px at 96 DPI)
      container: '',
      width: 151,
      height: 113,
      qrSize: 50,
      nameSize: 'text-xs',
      positionSize: 'text-xs',
      orgSize: 'text-xs',
      padding: 'p-3',
      spacing: 'gap-2'
    },
    normal: {
      // 2" x 1.5" standard name tag sticker (192px x 144px at 96 DPI)
      container: '',
      width: 192,
      height: 144,
      qrSize: 60,
      nameSize: 'text-sm',
      positionSize: 'text-xs',
      orgSize: 'text-xs',
      padding: 'p-4',
      spacing: 'gap-2'
    },
    medium: {
      // 2.5" x 1.5" larger name tag (240px x 144px at 96 DPI)
      container: '',
      width: 240,
      height: 144,
      qrSize: 64,
      nameSize: 'text-sm',
      positionSize: 'text-xs',
      orgSize: 'text-xs',
      padding: 'p-4',
      spacing: 'gap-3'
    },
    '60x40mm': {
      // 60mm × 40mm sticker (226px × 151px at 96 DPI)
      container: '',
      width: 226,
      height: 151,
      qrSize: 64,
      nameSize: 'text-sm',
      positionSize: 'text-xs',
      orgSize: 'text-xs',
      padding: 'p-4',
      spacing: 'gap-3'
    },
    large: {
      // 3" x 2" large event sticker (288px x 192px at 96 DPI)
      container: '',
      width: 288,
      height: 192,
      qrSize: 76,
      nameSize: 'text-base',
      positionSize: 'text-sm',
      orgSize: 'text-sm',
      padding: 'p-6',
      spacing: 'gap-4'
    },
    '80x50mm': {
      // 80mm × 50mm sticker (302px × 189px at 96 DPI)
      container: '',
      width: 302,
      height: 189,
      qrSize: 80,
      nameSize: 'text-base',
      positionSize: 'text-sm',
      orgSize: 'text-sm',
      padding: 'p-6',
      spacing: 'gap-4'
    }
  };

  const config = sizeConfig[size];

  // Use custom dimensions or preset dimensions (both in pixels for accurate printing)
  const actualWidth = customWidth || config.width;
  const actualHeight = customHeight || config.height;

  // Calculate QR code size based on actual dimensions (proportional to sticker size)
  const qrSize = customWidth && customHeight
    ? Math.min(Math.floor(Math.min(customWidth, customHeight) * 0.35), 100) // 35% of smaller dimension, max 100px for better scanning
    : config.qrSize;

  // Auto-scale font sizes based on text length if enabled
  const getFittedFontSize = (text, baseFontSize, maxWidthPx, minFontSize = 8) => {
    if (!autoScaleFont || !text) return baseFontSize;
    if (!maxWidthPx || maxWidthPx <= 0) return baseFontSize;

    const len = String(text).length;
    if (len <= 1) return baseFontSize;

    const averageCharWidthFactor = 0.56;
    const estimatedWidthAtBase = len * baseFontSize * averageCharWidthFactor;
    if (estimatedWidthAtBase <= maxWidthPx) return baseFontSize;

    const fitted = Math.floor(maxWidthPx / (len * averageCharWidthFactor));
    return Math.max(Math.min(baseFontSize, fitted), minFontSize);
  };

  // Determine font sizes (custom or default with auto-scaling)
  const getDefaultFontSize = (type) => {
    const dimension = Math.min(actualWidth, actualHeight);
    if (dimension < 120) return type === 'name' ? 10 : 8;
    if (dimension < 180) return type === 'name' ? 14 : 11;
    if (dimension < 240) return type === 'name' ? 16 : 12;
    return type === 'name' ? 18 : 14;
  };

  const baseNameFontSize = nameFontSize || getDefaultFontSize('name');
  const basePositionFontSize = positionFontSize || getDefaultFontSize('position');
  const baseOrgFontSize = orgFontSize || getDefaultFontSize('org');

  // Determine layout based on QR position
  const isHorizontal = qrPosition === 'left' || qrPosition === 'right';
  const flexDirection = isHorizontal ? 'row' : 'column';

  const estimatedOuterGap = Math.max(actualHeight * 0.04, 6);
  const estimatedInnerGap = Math.max(actualHeight * 0.03, 3);

  const estimatedContentPadding = Math.max(Math.round(Math.min(actualWidth, actualHeight) * 0.1), 12);

  const maxTextWidth = isHorizontal
    ? Math.max(actualWidth - qrSize - estimatedOuterGap - (estimatedContentPadding * 2), 40)
    : Math.max(actualWidth - (estimatedContentPadding * 2), 40);

  // Allow text to wrap up to ~2.5 lines before scaling down significantly
  // This prevents the font from becoming tiny just to fit on one line
  const wrapFactor = 2.5;
  const actualNameFontSize = getFittedFontSize(stickerData.name?.toUpperCase?.() ? stickerData.name.toUpperCase() : stickerData.name, baseNameFontSize, maxTextWidth * wrapFactor, 9);
  const actualPositionFontSize = getFittedFontSize(stickerData.position, basePositionFontSize, maxTextWidth * wrapFactor, 8);
  const actualOrgFontSize = getFittedFontSize(stickerData.organization, baseOrgFontSize, maxTextWidth * wrapFactor, 8);

  // Layout configurations matching your image
  const layouts = {
    classic: {
      background: 'bg-gradient-to-br from-purple-500 to-purple-600',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    corporate: {
      background: 'bg-gradient-to-br from-blue-600 to-blue-700',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    modern: {
      background: 'bg-gradient-to-br from-gray-800 to-gray-900',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    minimal: {
      background: 'bg-white border-2 border-gray-300',
      textColor: 'text-gray-900',
      qrBg: 'bg-gray-50',
      qrPosition: 'top-left'
    },
    tech: {
      background: 'bg-gradient-to-br from-green-600 to-teal-600',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    elegant: {
      background: 'bg-gradient-to-br from-indigo-600 to-purple-600',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    'thermal-minimal': {
      background: 'bg-white border-2 border-black',
      textColor: 'text-black',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    'thermal-bold': {
      background: 'bg-white border-4 border-black',
      textColor: 'text-black font-black',
      qrBg: 'bg-white border-2 border-black',
      qrPosition: 'top-left'
    },
    'thermal-modern': {
      background: 'bg-black',
      textColor: 'text-white',
      qrBg: 'bg-white border-2 border-black',
      qrPosition: 'top-left'
    }
  };

  const darkLayouts = {
    classic: {
      background: 'bg-gradient-to-br from-purple-800 to-purple-950',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    corporate: {
      background: 'bg-gradient-to-br from-blue-800 to-blue-950',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    modern: {
      background: 'bg-gradient-to-br from-gray-900 to-black',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    minimal: {
      background: 'bg-white border-2 border-black',
      textColor: 'text-black',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    tech: {
      background: 'bg-gradient-to-br from-green-800 to-teal-950',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    elegant: {
      background: 'bg-gradient-to-br from-indigo-800 to-purple-950',
      textColor: 'text-white',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    'thermal-minimal': {
      background: 'bg-white border-2 border-black',
      textColor: 'text-black',
      qrBg: 'bg-white',
      qrPosition: 'top-left'
    },
    'thermal-bold': {
      background: 'bg-white border-4 border-black',
      textColor: 'text-black font-black',
      qrBg: 'bg-white border-2 border-black',
      qrPosition: 'top-left'
    },
    'thermal-modern': {
      background: 'bg-black',
      textColor: 'text-white',
      qrBg: 'bg-white border-2 border-black',
      qrPosition: 'top-left'
    }
  };

  const layoutStyle = (colorMode === 'dark' ? darkLayouts : layouts)[layout] || (colorMode === 'dark' ? darkLayouts.classic : layouts.classic);

  const outerBorderColor = colorMode === 'dark'
    ? 'rgba(255, 255, 255, 0.22)'
    : 'rgba(0, 0, 0, 0.14)';


  // Memoize QR code URL for better performance
  const qrCodeValue = useMemo(() =>
    `${window.location.origin}/attendee/${stickerData.attendeeId}?event=${stickerData.eventId || ''}`,
    [stickerData.attendeeId, stickerData.eventId]
  );

  // QR Code Component
  const QRCodeElement = showQR ? (
    <div
      className={`${layoutStyle.qrBg} rounded-lg shadow-sm flex items-center justify-center`}
      style={{
        padding: `${Math.max(actualHeight * 0.015, 2)}px`,
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '10px',
        display: 'flex'
      }}
    >
      <QRCode
        value={qrCodeValue}
        size={qrSize}
        level="H"
        includeMargin={false}
        renderAs="svg"
        imageSettings={{
          src: '', // Optional: add logo src if needed, or remove imageSettings
          height: 0,
          width: 0,
          excavate: true,
        }}
      />
    </div>
  ) : null;

  // Logo Component
  const LogoElement = showLogo && logoUrl ? (
    <img
      src={logoUrl}
      alt="Logo"
      style={{
        width: `${logoSize}px`,
        height: `${logoSize}px`,
        objectFit: 'contain',
        display: 'block',
        margin: '0 auto'
      }}
    />
  ) : null;

  // Text Content Component
  const TextContent = (
    <div className="flex flex-col justify-center items-center text-center w-full" style={{
      gap: `${estimatedInnerGap}px`,
      maxWidth: '100%',
      padding: `0 ${estimatedContentPadding}px`,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {/* Name - Dynamic font weight */}
      <h2 className={`${layoutStyle.textColor} text-center w-full`}
        style={{
          fontSize: `${actualNameFontSize}px`,
          fontWeight: getFontWeightValue(nameFontWeight),
          lineHeight: lineHeight,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          margin: 0,
          padding: 0,
          textAlign: 'center'
        }}>
        {decodeHtml(stickerData.name || '').toUpperCase()}
      </h2>

      {/* Position - Dynamic font weight */}
      {showPosition && stickerData.position && (
        <p className={`${layoutStyle.textColor} ${colorMode === 'dark' ? 'opacity-100' : 'opacity-90'} text-center w-full`}
          style={{
            fontSize: `${actualPositionFontSize}px`,
            fontWeight: getFontWeightValue(positionFontWeight),
            lineHeight: lineHeight,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            margin: 0,
            padding: 0,
            textAlign: 'center'
          }}>
          {decodeHtml(stickerData.position)}
        </p>
      )}

      {/* Organization - Dynamic font weight */}
      {showOrganization && stickerData.organization && (
        <p className={`${layoutStyle.textColor} ${colorMode === 'dark' ? 'opacity-100' : 'opacity-80'} text-center w-full`}
          style={{
            fontSize: `${actualOrgFontSize}px`,
            fontWeight: getFontWeightValue(orgFontWeight),
            lineHeight: lineHeight,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            margin: 0,
            padding: 0,
            textAlign: 'center'
          }}>
          {decodeHtml(stickerData.organization)}
        </p>
      )}
    </div>
  );

  return (
    <div
      className={`${layoutStyle.background} ${config.padding} ${className}`}
      style={{
        width: customWidth === '100%' ? '100%' : `${actualWidth}px`,
        height: customHeight === '100%' ? '100%' : `${actualHeight}px`,
        border: layout === 'minimal' ? `1px solid ${outerBorderColor}` : 'none',
        borderRadius: '12px',
        overflow: 'hidden',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
        colorAdjust: 'exact',
        display: 'flex',
        flexDirection: flexDirection,
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${estimatedOuterGap}px`,
        boxSizing: 'border-box'
      }}
    >
      <div className="flex justify-center items-center text-center" style={{
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        flexDirection: flexDirection,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: `${estimatedInnerGap}px`
      }}>
        {/* Top or Left position */}
        {(qrPosition === 'top' || qrPosition === 'left') && (
          <>
            {LogoElement}
            {QRCodeElement}
          </>
        )}

        {/* Text content in the middle */}
        {TextContent}

        {/* Bottom or Right position */}
        {(qrPosition === 'bottom' || qrPosition === 'right') && (
          <>
            {QRCodeElement}
            {LogoElement}
          </>
        )}
      </div>
    </div>
  );
});

export default StickerPreview;
